import React, { useEffect, useState } from "react";
import config from '../config.js';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  formatDate, formatDisplayDate, parseISODate, getHoursFromServer, getTaskColorClass, isWeekend,
  getDedupedAlerts, shiftConfigFromApi, effectiveShift, hoursForToggle, hoursToSave, jobScheduleTasks, partTaskMatches,
  tasksByDate as groupTasksByDate,
} from '../features/scheduling/scheduleRules';
import {
  loadStationQueue as readStationQueue, loadAllStationsTasks, loadShiftConfig, loadJobSchedule, resolveJobInput,
  overrideTaskDate, clearTaskOverride, setDayLock, saveDayOrder, clearDayOrder,
  saveSecondShift, saveSecondShiftDefault, secondShiftDefaultQuestion, clearSecondShiftOverride,
} from '../features/scheduling/scheduleData';
import { desktopUi } from '../features/desktopUi';

/**
 * SchedulingPage
 *
 * Shows a tabbed interface for:
 * 1) Station-based scheduling (Day/Week view),
 * 2) All-station combined scheduling,
 * 3) Job-based scheduling for manual overrides,
 * 4) Part-based scheduling for manual overrides (NEW),
 * 5) Recommended (deadline-driven) schedule ignoring daily constraints.
 *
 * Includes:
 * - "Auto-Schedule All" button => calls POST /schedule
 * - GET /queue/{station} => tasks for station-based tab
 * - GET /jobs/{job_id}/progress => tasks for one job
 * - PATCH /tasks/{task_id}/manual_schedule => override scheduled_date
 * - GET /deadline_analysis => recommended schedule ignoring daily capacity
 * - POST /schedule/lock_day => Locks/Unlocks a specific day to prevent auto-scheduling
 *
 * - Color coding: priority=1 => pink, late => red
 * - Over capacity => red bold text
 * - By default, weekends are skipped in week views (toggle "Show Weekends").
 * - Extra fields: Master Job #, Product ID, Product Description, Qty, Status
 * - Button: "View Job" to switch to Job tab for that specific job.
 */

// ========================= Helper Functions =========================
// formatDate, formatDisplayDate, parseISODate, intervalToHours, getHoursFromServer, getTaskColorClass,
// isWeekend, getDedupedAlerts: features/scheduling/scheduleRules.js (shared with the phone).

function CapacityGauge({ hours, maxHours = 8 }) {
  const usage = hours > 0 ? hours : 0;
  const safeMax = maxHours > 0 ? maxHours : 1;
  const usagePct = Math.min((usage / safeMax) * 100, 100);
  const barColor = usage > safeMax ? "red" : "green";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <span style={{ fontWeight: "bold" }}>Capacity:</span>
      <div
        style={{
          width: "100px",
          height: "20px",
          border: "1px solid #ccc",
          backgroundColor: "#eee",
        }}
        title={`${usage.toFixed(1)} / ${maxHours.toFixed(1)} hours`}
      >
        <div
          style={{
            width: usagePct + "%",
            height: "100%",
            backgroundColor: barColor,
          }}
        />
      </div>
    </div>
  );
}


// Second-Shift inline control rendered next to the Schedule-Lock toggle on the
// Day view. Operator can toggle the per-(station, date) override, edit the
// hours, save it as the global weekday default, or clear an override.
function SecondShiftControl({
  station,
  dateObj,
  effective,
  hasOverride,
  busy,
  onToggle,
  onSaveDefault,
  onClearOverride,
}) {
  // Local hours state lets the operator type freely without firing a PUT on
  // every keystroke. Synced from the effective state when it changes.
  const [hoursInput, setHoursInput] = React.useState(
    String(effective?.hours ?? 0)
  );
  React.useEffect(() => {
    setHoursInput(String(effective?.hours ?? 0));
  }, [effective?.hours, station, dateObj]);

  const enabled = !!effective?.enabled;

  function commitHours() {
    const hrs = hoursToSave(hoursInput, effective); // 0-12, null = unchanged (scheduleRules)
    if (hrs == null) return;
    onToggle(station, dateObj, enabled, hrs);
  }

  return (
    <div className="d-inline-block align-items-center ms-4">
      <div className="form-check form-switch d-inline-block">
        <input
          className="form-check-input"
          type="checkbox"
          id="secondShiftSwitch"
          checked={enabled}
          disabled={busy}
          onChange={(e) => {
            const newEnabled = e.target.checked;
            onToggle(station, dateObj, newEnabled, hoursForToggle(effective, newEnabled)); // 4 h when it starts at 0
          }}
        />
        <label
          className="form-check-label fw-bold"
          htmlFor="secondShiftSwitch"
          style={{ color: enabled ? "#0a7" : "#666" }}
        >
          {enabled ? `🌙 Second Shift (${effective.hours}h)` : "🌙 Second Shift"}
        </label>
      </div>

      <input
        type="number"
        min="0"
        max="12"
        step="0.5"
        value={hoursInput}
        disabled={busy || !enabled}
        onChange={(e) => setHoursInput(e.target.value)}
        onBlur={commitHours}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        style={{ width: 70, marginLeft: 8 }}
        className="form-control form-control-sm d-inline-block"
        title="Second-shift hours (0–12)"
      />
      <span className="text-muted ms-1" style={{ fontSize: "0.8em" }}>hrs</span>

      <button
        type="button"
        className="btn btn-sm btn-outline-secondary ms-2"
        onClick={() => onSaveDefault(station, dateObj)}
        disabled={busy}
        title="Apply this state as the global default for every weekday until changed"
      >
        Save as default
      </button>

      {hasOverride && (
        <button
          type="button"
          className="btn btn-sm btn-link text-muted ms-1"
          onClick={() => onClearOverride(station, dateObj)}
          disabled={busy}
          title="Remove the per-day override and revert this date to the global default"
        >
          Clear override
        </button>
      )}

      <span
        className="ms-2 text-muted"
        style={{ fontSize: "0.85em", cursor: "help" }}
        title={
          "Second Shift adds extra capacity to the day's auto-scheduling.\n" +
          "Toggling here creates a per-day override for the current station+date.\n" +
          "Save as default applies the current state to every weekday for this station.\n" +
          "Source: " + (effective?.source || "off")
        }
      >
        ⓘ
      </span>
    </div>
  );
}


function PastDueInboundBanner({ items }) {
  if (!items || items.length === 0) return null;
  const totalSheets = items.reduce((a, it) => a + (it.overdue_sheets || 0), 0);
  return (
    <div
      className="alert alert-warning mb-3"
      style={{ border: "1px solid #ffcc00", backgroundColor: "#fff8e1" }}
    >
      <strong>⚠ Past-due inbound POs not counted in availability forecast</strong>
      <div style={{ fontSize: "0.9em", marginTop: "4px" }}>
        {items.length} material{items.length === 1 ? "" : "s"} with open POs whose estimated receive date has passed
        (<strong>{totalSheets}</strong> sheet{totalSheets === 1 ? "" : "s"} total).
        Update the PO's ETA or mark it received for it to return to the forecast.
      </div>
      <ul style={{ margin: "6px 0 0 0", paddingLeft: "20px", fontSize: "0.9em" }}>
        {items.slice(0, 10).map((it) => (
          <li key={it.material_id}>
            {it.material_description}: <strong>{it.overdue_sheets}</strong> sheet
            {it.overdue_sheets === 1 ? "" : "s"} on {it.overdue_po_count} PO
            {it.overdue_po_count === 1 ? "" : "s"}
            {it.earliest_overdue_eta ? ` (earliest ETA ${it.earliest_overdue_eta})` : ""}
            {it.po_numbers && it.po_numbers.length > 0 ? ` — PO${it.po_numbers.length === 1 ? "" : "s"} ${it.po_numbers.join(", ")}` : ""}
          </li>
        ))}
        {items.length > 10 && <li>…and {items.length - 10} more</li>}
      </ul>
    </div>
  );
}

function SchedulingAlertsBox({ alerts }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: "5px",
      }}
    >
      <div
        style={{
          width: "675px",
          backgroundColor: "var(--jq-bg-card, #fff)",
          border: "1px solid #ccc",
          borderRadius: "4px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            backgroundColor: "var(--jq-bg-subtle, #f5f5f5)",
            padding: "8px",
            borderBottom: "1px solid #ccc",
            fontWeight: "bold",
          }}
        >
          Alerts
        </div>

        <div style={{ maxHeight: "200px", overflowY: "auto", padding: "8px" }}>
          {!alerts || alerts.length === 0 ? (
            <div style={{ color: "#999", fontStyle: "italic" }}>
              No alerts.
            </div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {alerts.map((alert, idx) => (
                <li
                  key={idx}
                  style={{ marginBottom: "6px", whiteSpace: "pre-wrap" }}
                >
                  {alert.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function BacklogPressureBox({ backlog }) {
  if (!backlog || backlog.length === 0) return null;
  backlog.sort((a, b) => b.hours - a.hours);

  return (
    <div className="mb-3">
      <h5>
        ⚠️ Current Backlog – Tasks Due Today or Earlier
        <span
          title="This shows total task hours at each station for jobs due today or earlier that have not yet been completed."
          style={{ marginLeft: "6px", cursor: "help", color: "#888" }}
        >
          ⓘ
        </span>
      </h5>
      <p className="text-muted" style={{ marginTop: "-8px", fontSize: "0.9rem" }}>
        Shows estimated hours still required at each station for overdue jobs.
      </p>
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Station</th>
            <th>Hours Overdue</th>
          </tr>
        </thead>
        <tbody>
          {backlog.map((row, idx) => (
            <tr key={idx} className={row.hours > 8 ? "text-danger" : ""}>
              <td>{row.station}</td>
              <td>{row.hours.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-muted" style={{ fontSize: "0.85rem" }}>
        Red rows indicate stations with over 8 hours of overdue work.
      </p>
    </div>
  );
}

function SchedulingFAQ() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mb-4">
      <div
        style={{
          cursor: "pointer",
          color: "#007bff",
          textDecoration: "underline",
          fontWeight: "bold",
          marginBottom: "6px",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        📘 Scheduling Metrics Explained {expanded ? "▲" : "▼"}
      </div>

      {expanded && (
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "12px",
            backgroundColor: "var(--jq-bg-surface, #f9f9f9)",
            fontSize: "0.95rem",
            lineHeight: "1.5",
          }}
        >
          <h6><strong>Current Backlog – Tasks Due Today or Earlier</strong></h6>
          <p>
            This shows the number of task-hours still required at each station
            to complete **jobs that are already overdue** (due today or earlier).
          </p>
          <ul>
            <li>Ignores task dependencies.</li>
            <li>Includes both scheduled and unscheduled tasks.</li>
          </ul>

          <h6 className="mt-3"><strong>Recommended (Ignoring Normal Constraints)</strong></h6>
          <p>
            This forecast shows how many task-hours per station you’d need to complete **today**
            (or this week) to finish every open job on time, based on back-scheduling from the due date.
          </p>
          <ul>
            <li>Respects order of operations.</li>
            <li>Ignores material limits, shift constraints, and station availability.</li>
          </ul>
        </div>
      )}
    </div>
  );
}



// ========================= Main SchedulingPage Component =========================

export default function SchedulingPage() {
  // Tabs: "station" | "all" | "job" | "part" | "recommended"
  const [activeTab, setActiveTab] = useState("station");

  // Station-based scheduling
  const stationList = ["Laser", "Tap", "Brake", "Fabrication", "Blast", "Finish"];
  const [selectedStation, setSelectedStation] = useState("Laser");
  const [stationTasks, setStationTasks] = useState([]);
  // Day-level schedule locks for the selected station — a Set of "YYYY-MM-DD"
  // strings loaded from GET /schedule/locks. Drives the lock indicator so it
  // reflects the authoritative schedule_locks records, not per-task flags.
  const [lockedDates, setLockedDates] = useState(() => new Set());
  const [stationLoading, setStationLoading] = useState(false);

  // Day vs. Week toggle for station/all
  const [viewMode, setViewMode] = useState("day");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showWeekends, setShowWeekends] = useState(false);

  // Job-based scheduling
  const [jobIdInput, setJobIdInput] = useState("");
  const [jobData, setJobData] = useState(null);
  const [jobLoading, setJobLoading] = useState(false);

  // Part-based scheduling (NEW)
  const [partInput, setPartInput] = useState("");
  const [partTasks, setPartTasks] = useState([]);
  const [partLoading, setPartLoading] = useState(false);

  // All-stations combined
  const [allStationsTasks, setAllStationsTasks] = useState([]);
  const [allStationsLoading, setAllStationsLoading] = useState(false);

  // Scheduling alerts from server
  const [schedulingAlerts, setSchedulingAlerts] = useState([]);
  const [alertsByJob, setAlertsByJob] = useState({});

  // Past-due inbound POs — materials with open POs whose ETA has already passed.
  // These sheets are NOT counted in availability forecast until PO is received
  // or ETA is updated. Operators must act on them.
  const [pastDueInbound, setPastDueInbound] = useState([]);
  
  // State for loading indicator during auto-scheduling
  const [isScheduling, setIsScheduling] = useState(false);
  const [forecastType, setForecastType] = useState('deadline'); // 'deadline' or 'leveled'

  // Recommended schedule states
  const [recommendedViewMode, setRecommendedViewMode] = useState("day");
  const [recommendedDate, setRecommendedDate] = useState(new Date());
  const [recommendedShowWeekends, setRecommendedShowWeekends] = useState(false);
  const [recommendedData, setRecommendedData] = useState([]);  
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [backlogPressure, setBacklogPressure] = useState([]);

  // Suggestion State for Part Search
  const [partSuggestions, setPartSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Second-shift configuration: per-station global defaults + per-(station, date) overrides.
  // Loaded once on mount and refreshed after any toggle/save. Drives the inline second-shift
  // controls and the dynamic CapacityGauge maxHours.
  const [shiftDefaults, setShiftDefaults] = useState({}); // { Laser: {enabled, hours}, ... }
  const [shiftOverrides, setShiftOverrides] = useState({}); // { "Laser|2026-04-30": {enabled, hours, note} }
  const [firstShiftHours, setFirstShiftHours] = useState(7);
  const [secondShiftBusy, setSecondShiftBusy] = useState(false);

  // Bulk edit (Station tab — day + week view). Lets the operator check multiple
  // task rows and reschedule them all to one date (or clear all overrides) in
  // one shot, instead of editing each row individually.
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState(() => new Set());
  const [bulkDate, setBulkDate] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const API_BASE = config.API_BASE_URL;
  const SCHEDULER_API_ENDPOINT = "https://dzum4n96a9.execute-api.us-east-2.amazonaws.com/default/jobq-start-scheduler-api";

  // 1) On component mount, fetch existing alerts so they persist after refresh
  useEffect(() => {
    async function fetchAlertsOnLoad() {
      try {
        const alertsRes = await fetch(`${API_BASE}/scheduling/alerts`);
        if (alertsRes.ok) {
          const data = await alertsRes.json();
          setSchedulingAlerts(getDedupedAlerts(data.alerts || []));
        }
      } catch (err) {
        console.error("Error fetching scheduling alerts on page load:", err);
      }
      try {
        const pdRes = await fetch(`${API_BASE}/materials/past_due_inbound`);
        if (pdRes.ok) {
          setPastDueInbound(await pdRes.json());
        }
      } catch (err) {
        console.error("Error fetching past-due inbound POs:", err);
      }
    }
    fetchAlertsOnLoad();
    fetchShiftConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch defaults + overrides for the upcoming horizon. Called on mount and
  // after any toggle/save so the inline second-shift controls stay in sync.
  async function fetchShiftConfig() {
    try {
      const data = await loadShiftConfig(); // features/scheduling/scheduleData.js
      if (!data) return;
      const cfg = shiftConfigFromApi(data);
      setShiftDefaults(cfg.defaults);
      setShiftOverrides(cfg.overrides);
      if (cfg.firstShiftHours != null) {
        setFirstShiftHours(cfg.firstShiftHours);
      }
    } catch (err) {
      console.error("Error fetching shift config:", err);
    }
  }

  // Resolve effective second shift for a (station, date):
  //   override row (date-specific) > weekday default > off.
  function getEffectiveShift(station, dateObj) {
    return effectiveShift(shiftDefaults, shiftOverrides, station, dateObj); // scheduleRules
  }

  // Toggle second-shift state for the current (station, date) — writes a
  // per-day override. Optimistically updates state, then refetches to confirm.
  async function handleSecondShiftToggle(station, dateObj, newEnabled, newHours) {
    if (secondShiftBusy) return;
    const dateStr = formatDate(dateObj);
    setSecondShiftBusy(true);
    try {
      const r = await saveSecondShift({ station, date: dateStr, enabled: newEnabled, hours: newHours, ui: desktopUi }); // scheduleData
      if (r.done) await fetchShiftConfig();
    } finally {
      setSecondShiftBusy(false);
    }
  }

  // Promote the current (station, date)'s second-shift state to the global
  // weekday default for that station, then clear the per-day override so the
  // current view reflects the new default cleanly.
  async function handleSaveSecondShiftDefault(station, dateObj) {
    if (secondShiftBusy) return;
    const eff = getEffectiveShift(station, dateObj);
    const dateStr = formatDate(dateObj);
    if (!window.confirm(secondShiftDefaultQuestion(station, eff))) {
      return;
    }
    setSecondShiftBusy(true);
    try {
      const r = await saveSecondShiftDefault({ station, date: dateStr, effective: eff, ui: desktopUi }); // scheduleData
      if (r.done) await fetchShiftConfig();
    } finally {
      setSecondShiftBusy(false);
    }
  }

  // Clear a per-day override → revert to the global default.
  async function handleClearSecondShiftOverride(station, dateObj) {
    if (secondShiftBusy) return;
    const dateStr = formatDate(dateObj);
    setSecondShiftBusy(true);
    try {
      const r = await clearSecondShiftOverride({ station, date: dateStr, ui: desktopUi }); // scheduleData
      if (r.done) await fetchShiftConfig();
    } finally {
      setSecondShiftBusy(false);
    }
  }

  // 2) Build job-based dictionary of alerts
  useEffect(() => {
    const newAlertsByJob = {};
    schedulingAlerts.forEach((al) => {
      const jId = al.job_id;
      if (!newAlertsByJob[jId]) {
        newAlertsByJob[jId] = [];
      }
      newAlertsByJob[jId].push(al);
    });
    setAlertsByJob(newAlertsByJob);
  }, [schedulingAlerts]);

  function handleViewJob(jobId) {
    setActiveTab("job");
    setJobIdInput(String(jobId || ""));
    if (jobId) {
      loadJobData(jobId);
    }
  }

  // ========== FETCHES ==========

  async function loadStationQueue(station) {
    setStationLoading(true);
    try {
      // features/scheduling/scheduleData.js: the open tasks (+ laser timeline), then the day locks, which
      // drive the lock indicator from the authoritative schedule_locks records (not per-task flags).
      const r = await readStationQueue(station, { onTasks: setStationTasks });
      if (r.failed) {
        console.error("Failed to fetch station queue:", r.status);
        setStationTasks([]);
        return;
      }
      if (r.lockedDates) setLockedDates(r.lockedDates);
    } catch (err) {
      console.error("Error loading station queue:", err);
      setStationTasks([]);
    } finally {
      setStationLoading(false);
    }
  }

  async function loadAllStations() {
    setAllStationsLoading(true);
    try {
      const tasks = await loadAllStationsTasks(stationList); // scheduleData: one station after another
      setAllStationsTasks(tasks);
      return tasks; // Return for reuse by Part Tab
    } catch (err) {
      console.error("Error loading all-station tasks:", err);
      setAllStationsTasks([]);
      return [];
    } finally {
      setAllStationsLoading(false);
    }
  }

  async function loadJobData(jobId) {
    setJobLoading(true);
    try {
      setJobData(await loadJobSchedule(jobId)); // scheduleData (null when the job isn't read)
    } catch (err) {
      console.error("Error loading job data:", err);
      setJobData(null);
    } finally {
      setJobLoading(false);
    }
  }

  async function loadRecommendedSchedule() {
    setRecommendedLoading(true);
    try {
      const skipParam = recommendedShowWeekends ? "false" : "true";
      const endpoint = forecastType === 'leveled' 
        ? 'leveled_production_plan' 
        : 'deadline_analysis';

      const url = `${API_BASE}/${endpoint}?skip_weekends=${skipParam}`;

      const res = await fetch(url);
      if (!res.ok) {
        console.error(`Failed to fetch ${endpoint}:`, res.status);
        setRecommendedData([]);
        return;
      }
      const data = await res.json();
      setRecommendedData(data);
    } catch (err) {
      console.error("Error loading recommended schedule:", err);
      setRecommendedData([]);
    } finally {
      setRecommendedLoading(false);
    }
  }

  async function handlePartLookup() {
      if (!partInput) return;
      setPartLoading(true);
      setPartTasks([]);

      try {
        // 1. Ensure we have the pool of tasks
        let allTasks = allStationsTasks;
        if (!allTasks || allTasks.length === 0) {
          allTasks = await loadAllStations(); 
        }

        // Exact matches when there are any, else partial; sorted by day then job (scheduleRules).
        const filtered = partTaskMatches(allTasks, partInput);

        setPartTasks(filtered);

      } catch (err) {
        console.error("Error searching parts:", err);
        alert("Failed to load part tasks.");
      } finally {
        setPartLoading(false);
      }
    }

  // Manual schedule override
  async function handleManualOverride(taskId, newDate) {
    // scheduleData.overrideTaskDate asks, saves and says how it went (word for word as before).
    const r = await overrideTaskDate({ taskId, date: newDate, ui: desktopUi });
    if (r.done) {
      // reload relevant tab
      if (activeTab === "job" && jobData?.job_id) {
        loadJobData(jobData.job_id);
      } else if (activeTab === "station") {
        loadStationQueue(selectedStation);
      } else if (activeTab === "all") {
        loadAllStations();
      } else if (activeTab === "recommended") {
        loadRecommendedSchedule();
      } else if (activeTab === "part") {
        // Just reload the part tab search
        handlePartLookup(); 
      }
    }
  }

  // --- Bulk edit helpers (Station tab) ---

  // Toggle bulk mode on/off. Turning off clears any pending selection so the
  // operator can't accidentally apply a stale selection after they hide the UI.
  function toggleBulkMode() {
    setBulkEditMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedTaskIds(new Set());
        setBulkDate("");
      }
      return next;
    });
  }

  function toggleTaskSelection(taskId) {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }

  function selectTaskIds(taskIds, selected) {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      taskIds.forEach((id) => {
        if (selected) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }

  function clearSelection() {
    setSelectedTaskIds(new Set());
  }

  // Refetch whichever tab/data is currently displayed. Used after any
  // schedule mutation (single OR bulk) so the operator sees the result.
  function refetchActiveTab() {
    if (activeTab === "job" && jobData?.job_id) {
      loadJobData(jobData.job_id);
    } else if (activeTab === "station") {
      loadStationQueue(selectedStation);
    } else if (activeTab === "all") {
      loadAllStations();
    } else if (activeTab === "recommended") {
      loadRecommendedSchedule();
    } else if (activeTab === "part") {
      handlePartLookup();
    }
  }

  // Render YYYY-MM-DD as "Thursday, May 28, 2026" for confirm prompts so
  // the operator can spot wrong-week picks before applying.
  function formatBulkDateForConfirm(dateStr) {
    if (!dateStr) return dateStr;
    const [y, m, d] = dateStr.split("-").map(Number);
    if (!y || !m || !d) return dateStr;
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  function todayLocalISO() {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  }

  async function applyBulkDate() {
    const ids = Array.from(selectedTaskIds);
    if (ids.length === 0) {
      alert("No tasks selected.");
      return;
    }
    if (!bulkDate) {
      alert("Pick a date to apply.");
      return;
    }

    const friendly = formatBulkDateForConfirm(bulkDate);
    const todayStr = todayLocalISO();
    if (bulkDate < todayStr) {
      const todayFriendly = formatBulkDateForConfirm(todayStr);
      if (
        !window.confirm(
          `WARNING: ${friendly} is in the PAST (today is ${todayFriendly}).\n\n` +
            `Are you sure you want to reschedule ${ids.length} task(s) to a date that has already passed?\n\n` +
            `Click OK to proceed anyway, or Cancel to pick a different date.`
        )
      ) {
        return;
      }
    } else if (!window.confirm(`Reschedule ${ids.length} task(s) to ${friendly}?`)) {
      return;
    }

    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        ids.map((tid) =>
          fetch(`${API_BASE}/tasks/${tid}/manual_schedule`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ scheduled_date: bulkDate }),
          }).then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return tid;
          })
        )
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      const ok = results.length - failed;
      if (failed === 0) {
        alert(`Override applied to ${ok} task(s).`);
        clearSelection();
      } else {
        alert(`Applied to ${ok} task(s). ${failed} failed — check console for details.`);
        results.forEach((r, i) => {
          if (r.status === "rejected") {
            console.error(`Bulk override failed for task ${ids[i]}:`, r.reason);
          }
        });
      }
      refetchActiveTab();
    } finally {
      setBulkBusy(false);
    }
  }

  async function applyBulkClear() {
    const ids = Array.from(selectedTaskIds);
    if (ids.length === 0) {
      alert("No tasks selected.");
      return;
    }
    if (!window.confirm(`Clear overrides on ${ids.length} task(s)? They will be auto-scheduled.`)) {
      return;
    }

    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        ids.map((tid) =>
          fetch(`${API_BASE}/tasks/${tid}/clear_override`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
          }).then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return tid;
          })
        )
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      const ok = results.length - failed;
      if (failed === 0) {
        alert(`Cleared override on ${ok} task(s).`);
        clearSelection();
      } else {
        alert(`Cleared on ${ok} task(s). ${failed} failed — check console for details.`);
        results.forEach((r, i) => {
          if (r.status === "rejected") {
            console.error(`Bulk clear failed for task ${ids[i]}:`, r.reason);
          }
        });
      }
      refetchActiveTab();
    } finally {
      setBulkBusy(false);
    }
  }

  // Clear manual override — returns task to auto-scheduler
  async function handleClearOverride(taskId) {
    const r = await clearTaskOverride({ taskId, ui: desktopUi }); // scheduleData
    if (r.done) {
      if (activeTab === "job" && jobData?.job_id) {
        loadJobData(jobData.job_id);
      } else if (activeTab === "station") {
        loadStationQueue(selectedStation);
      } else if (activeTab === "all") {
        loadAllStations();
      } else if (activeTab === "recommended") {
        loadRecommendedSchedule();
      } else if (activeTab === "part") {
        handlePartLookup();
      }
    }
  }

  // --- NEW: Toggle Lock for a Day ---
  async function handleToggleLock(isLocked) {
    if (!selectedDate) return;
    const dateStr = formatDate(selectedDate);
    // Lock only the current station being viewed; re-read the queue, then say so (scheduleData).
    await setDayLock({
      station: selectedStation, date: dateStr, locked: isLocked, ui: desktopUi,
      afterSave: () => loadStationQueue(selectedStation),
    });
  }

  // ---- Drag-to-reorder handlers (Day view) ----
  // Activation distance prevents drags from firing on simple clicks of the
  // View Job / Override buttons inside each row.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  async function handleDayDragEnd(event, currentTasks, station, dateObj) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = currentTasks.findIndex(t => t.task_id === active.id);
    const newIndex = currentTasks.findIndex(t => t.task_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(currentTasks, oldIndex, newIndex);
    const taskIds = reordered.map(t => t.task_id);
    const dateStr = formatDate(dateObj);

    // Optimistic UI update — apply pinned positions to local stationTasks
    // immediately. The server-backed refresh below confirms.
    setStationTasks(prev => prev.map(t => {
      const idx = taskIds.indexOf(t.task_id);
      if (idx >= 0) {
        return { ...t, manual_sort_position: idx + 1 };
      }
      return t;
    }));

    await saveDayOrder({ station, date: dateStr, taskIds, ui: desktopUi }); // scheduleData (the phone's up/down too)
    // Re-fetch authoritative order so any concurrent changes are picked up (after a refusal too).
    loadStationQueue(station);
  }

  async function handleClearReorder(station, dateObj) {
    const r = await clearDayOrder({ station, dateObj, date: formatDate(dateObj), ui: desktopUi }); // scheduleData
    if (r.done) loadStationQueue(station);
  }

  async function handlePartInputChange(val) {
    setPartInput(val);
    if (!val || val.length < 2) {
      setPartSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    try {
      // Calls the existing backend endpoint
      const res = await fetch(`${API_BASE}/parts/search?query=${encodeURIComponent(val)}`);
      if (res.ok) {
        const hits = await res.json();
        setPartSuggestions(hits);
        setShowSuggestions(true);
      }
    } catch (err) {
      console.error("Error fetching part suggestions:", err);
    }
  }

  function selectPartSuggestion(part) {
    // Set the input to the exact Product ID
    setPartInput(part.product_id);
    setPartSuggestions([]);
    setShowSuggestions(false);
    // Optional: Auto-trigger the lookup immediately
    // handlePartLookup(); 
  }

  // Run auto-schedule
  async function handleFullSchedule() {
    if (!window.confirm("Run a full auto-schedule now? This may rearrange many tasks.")) {
      return;
    }

    setIsScheduling(true); 

    try {
      const scheduleRes = await fetch(SCHEDULER_API_ENDPOINT, { method: "POST" });

      if (scheduleRes.status !== 202) {
        const errorText = await scheduleRes.text();
        alert(`Failed to start auto-schedule process: ${scheduleRes.status} - ${errorText}`);
        return;
      }
      
      alert("Scheduling process started. The data will refresh automatically in one minute.");

      setTimeout(() => {
        console.log("Refreshing data after scheduling...");
        
        fetch(`${API_BASE}/scheduling/alerts`)
            .then(res => res.ok ? res.json() : Promise.reject(res))
            .then(data => setSchedulingAlerts(getDedupedAlerts(data.alerts || [])))
            .catch(err => console.error("Error fetching alerts post-run:", err));

        if (activeTab === "station") {
          loadStationQueue(selectedStation);
        } else if (activeTab === "all") {
          loadAllStations();
        } else if (activeTab === "job" && jobData?.job_id) {
          loadJobData(jobData.job_id);
        } else if (activeTab === "recommended") {
          loadRecommendedSchedule();
        } else if (activeTab === "part") {
          handlePartLookup();
        }
      }, 60000); 

    } catch (err) {
      console.error("Error triggering the auto-schedule process:", err);
      alert("Error triggering the auto-schedule process.");
    } finally {
      setIsScheduling(false);
    }
  }

  // ====================== Summaries & Helpers ======================

  function renderDailyLaserSummary(tasksForDay) {
    const laserTasks = tasksForDay.filter((t) => t.workstation === "Laser");
    if (!laserTasks.length) return null;

    const usageByMaterial = {};
    laserTasks.forEach((t) => {
      const mat = t.material_description || "Unknown Material";
      usageByMaterial[mat] = (usageByMaterial[mat] || 0) + (t.sheet_required || 0);
    });

    return (
      <div
        style={{
          marginTop: "0.5rem",
          padding: "6px",
          border: "1px dashed #aaa",
          backgroundColor: "var(--jq-bg-subtle, #fafafa)",
        }}
      >
        <strong>Laser Material Usage Summary:</strong>
        <div style={{ marginLeft: "1rem", marginTop: "4px" }}>
          {Object.entries(usageByMaterial).map(([material, totalSheets]) => (
            <div key={material}>
              {material}: {totalSheets} total sheets
            </div>
          ))}
        </div>
      </div>
    );
  }

  function getWeekDates(anchorDate, showWknds) {
    const arr = [];
    let current = new Date(anchorDate);

    while (arr.length < 7) {
      if (!showWknds && isWeekend(current)) {
        current.setDate(current.getDate() + 1);
        continue;
      }
      arr.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return arr;
  }

  // =================== Station Tab (existing) ===================
  function renderStationTab() {
    const tasksByDate = groupTasksByDate(stationTasks); // by scheduled day, "Unscheduled" (scheduleRules)

    const dayLabel = formatDisplayDate(selectedDate);
    const unscheduledTasks = tasksByDate["Unscheduled"] || [];

    function renderUnscheduledBlock() {
      if (!unscheduledTasks.length) return null;
      return (
        <div
          style={{ marginBottom: "1rem", border: "1px solid #f99", padding: "6px" }}
        >
          <h5>Unscheduled Tasks ({unscheduledTasks.length})</h5>
          <div
            className="table-responsive"
            style={{ maxHeight: 320, overflowY: "auto" }}
          >
            <table className="table table-sm">
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "var(--bs-body-bg, #fff)",
                  zIndex: 1,
                  boxShadow: "inset 0 -1px 0 #dee2e6",
                }}
              >
                <tr>
                  <th>Master Job #</th>
                  <th>Product ID</th>
                  <th>Product Desc</th>
                  <th>Qty</th>
                  <th>Job ID</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Due</th>
                  <th>Hours</th>
                  <th>Sheets Needed</th>
                  <th>Material</th>
                  <th>Before</th>
                  <th>After</th>
                  <th>Override</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {unscheduledTasks.map((t, i) => (
                  <StationTaskRow
                    key={i}
                    task={t}
                    onOverride={handleManualOverride}
                    onClearOverride={handleClearOverride}
                    onViewJob={handleViewJob}
                    alertsByJob={alertsByJob}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (viewMode === "day") {
      const dayKey = formatDate(selectedDate);
      const dayTasks = (tasksByDate[dayKey] || []).filter(
        (t) => t.workstation !== "Shipping"
      );
      let sum = 0;
      dayTasks.forEach((tsk) => {
        sum += getHoursFromServer(tsk);
      });

      // --- Determine if the displayed day is "Locked" ---
      // Authoritative: a schedule_locks row exists for this (station, date). Works
      // even when the day is empty and never flips off when a new task is added.
      const isDayLocked = lockedDates.has(dayKey);

      // Resolve effective second-shift state for this (station, date).
      const effShift = getEffectiveShift(selectedStation, selectedDate);
      const dayCapacityMax = firstShiftHours + (effShift.enabled ? effShift.hours : 0);
      const overKey = `${selectedStation}|${dayKey}`;
      const hasOverride = !!shiftOverrides[overKey];

      return (
        <div>
          {renderUnscheduledBlock()}
          <div
            className="mb-2"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <strong>
                Viewing {selectedStation} on {dayLabel}
              </strong>

              {/* --- NEW: Lock Toggle --- */}
              <div className="d-inline-block align-items-center ms-4">
                  <div className="form-check form-switch d-inline-block">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="lockDaySwitch"
                      checked={isDayLocked}
                      onChange={(e) => handleToggleLock(e.target.checked)}
                    />
                    <label className="form-check-label fw-bold" htmlFor="lockDaySwitch" style={{ color: isDayLocked ? "red" : "#666" }}>
                      {isDayLocked ? "🔒 Schedule Locked" : "🔓 Auto-Schedule Enabled"}
                    </label>
                  </div>
                  <span
                    className="ms-2 text-muted"
                    style={{ fontSize: "0.85em", cursor: "help" }}
                    title="Locking freezes this station's day: the Auto-Scheduler won't move the tasks already on it AND won't add any new tasks to it (even if it is currently empty). Unlocking lets the Auto-Scheduler rearrange and fill it again."
                  >
                    ⓘ
                  </span>
              </div>

              {/* --- Second Shift inline controls --- */}
              <SecondShiftControl
                station={selectedStation}
                dateObj={selectedDate}
                effective={effShift}
                hasOverride={hasOverride}
                busy={secondShiftBusy}
                onToggle={handleSecondShiftToggle}
                onSaveDefault={handleSaveSecondShiftDefault}
                onClearOverride={handleClearSecondShiftOverride}
              />

              <div style={{ color: sum > dayCapacityMax ? "red" : "black", marginTop: "4px" }}>
                {sum.toFixed(1)} total hours
                {effShift.enabled && (
                  <span className="text-muted ms-2" style={{ fontSize: "0.85em" }}>
                    (capacity: {firstShiftHours}h first + {effShift.hours}h second
                    {effShift.source === "override" ? " · override" : " · default"})
                  </span>
                )}
              </div>
            </div>
            <CapacityGauge hours={sum} maxHours={dayCapacityMax} />
          </div>

          {dayTasks.some(t => t.manual_sort_position != null) && (
            <div className="mb-1" style={{ fontSize: "0.85em" }}>
              <span className="text-muted me-2">📌 Manual order in effect.</span>
              <button
                type="button"
                className="btn btn-sm btn-link p-0"
                onClick={() => handleClearReorder(selectedStation, selectedDate)}
                title="Clear all pinned positions for this station+date — tasks revert to algorithmic order."
              >
                Reset to auto order
              </button>
            </div>
          )}
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  {bulkEditMode && (
                    <th style={{ width: 28, textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={
                          dayTasks.length > 0 &&
                          dayTasks.every((t) => selectedTaskIds.has(t.task_id))
                        }
                        ref={(el) => {
                          if (el) {
                            const some = dayTasks.some((t) => selectedTaskIds.has(t.task_id));
                            const all = dayTasks.length > 0 && dayTasks.every((t) => selectedTaskIds.has(t.task_id));
                            el.indeterminate = some && !all;
                          }
                        }}
                        onChange={(e) =>
                          selectTaskIds(dayTasks.map((t) => t.task_id), e.target.checked)
                        }
                        title="Select all in this day"
                      />
                    </th>
                  )}
                  <th style={{ width: 28 }} />
                  <th>Master Job #</th>
                  <th>Product ID</th>
                  <th>Product Desc</th>
                  <th>Qty</th>
                  <th>Job ID</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Due</th>
                  <th>Hours</th>
                  <th>Scheduled Date</th>
                  <th>Sheets Needed</th>
                  <th>Material</th>
                  <th>Before</th>
                  <th>After</th>
                  <th />
                </tr>
              </thead>
              {dayTasks.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={bulkEditMode ? 17 : 16} className="text-muted">
                      No tasks scheduled for this day.
                    </td>
                  </tr>
                </tbody>
              ) : (
                <DndContext
                  sensors={dndSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => handleDayDragEnd(event, dayTasks, selectedStation, selectedDate)}
                >
                  <SortableContext
                    items={dayTasks.map(t => t.task_id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody>
                      {dayTasks.map((t) => (
                        <SortableStationTaskRow
                          key={t.task_id}
                          task={t}
                          onOverride={handleManualOverride}
                          onClearOverride={handleClearOverride}
                          onViewJob={handleViewJob}
                          alertsByJob={alertsByJob}
                          bulkMode={bulkEditMode}
                          selected={selectedTaskIds.has(t.task_id)}
                          onSelectToggle={toggleTaskSelection}
                        />
                      ))}
                    </tbody>
                  </SortableContext>
                </DndContext>
              )}
            </table>
          </div>

          {renderDailyLaserSummary(dayTasks)}
        </div>
      );
    } else {
      // Week view
      const weekDates = getWeekDates(selectedDate, showWeekends);

      return (
        <div>
          {renderUnscheduledBlock()}
          <strong>Viewing {selectedStation} for the selected week</strong>
          {weekDates.map((d) => {
            const dateKey = formatDate(d);
            const tasksForDay = (tasksByDate[dateKey] || []).filter(
              (t) => t.workstation !== "Shipping"
            );
            let sum = 0;
            tasksForDay.forEach((tsk) => {
              sum += getHoursFromServer(tsk);
            });

            return (
              <div
                key={dateKey}
                style={{
                  marginBottom: "1rem",
                  border: "1px solid #ccc",
                  padding: "6px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {(() => {
                    const dShift = getEffectiveShift(selectedStation, d);
                    const dMax = firstShiftHours + (dShift.enabled ? dShift.hours : 0);
                    return (
                      <>
                        <div>
                          <strong>
                            {formatDisplayDate(d)}{" "}
                            {isWeekend(d) && (
                              <span className="text-danger">(Weekend)</span>
                            )}
                            {dShift.enabled && (
                              <span className="badge bg-info ms-2" title={`Second shift +${dShift.hours}h (${dShift.source})`}>
                                +{dShift.hours}h 2nd
                              </span>
                            )}
                          </strong>
                          <div style={{ color: sum > dMax ? "red" : "black" }}>
                            {sum.toFixed(1)} total hours
                          </div>
                        </div>
                        <CapacityGauge hours={sum} maxHours={dMax} />
                      </>
                    );
                  })()}
                </div>

                {tasksForDay.length === 0 ? (
                  <div className="text-muted mt-1">No tasks scheduled.</div>
                ) : (
                  <div className="table-responsive mt-1">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          {bulkEditMode && (
                            <th style={{ width: 28, textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={tasksForDay.every((t) => selectedTaskIds.has(t.task_id))}
                                ref={(el) => {
                                  if (el) {
                                    const some = tasksForDay.some((t) => selectedTaskIds.has(t.task_id));
                                    const all = tasksForDay.every((t) => selectedTaskIds.has(t.task_id));
                                    el.indeterminate = some && !all;
                                  }
                                }}
                                onChange={(e) =>
                                  selectTaskIds(tasksForDay.map((t) => t.task_id), e.target.checked)
                                }
                                title="Select all in this day"
                              />
                            </th>
                          )}
                          <th>Master Job #</th>
                          <th>Product ID</th>
                          <th>Product Desc</th>
                          <th>Qty</th>
                          <th>Job ID</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Due</th>
                          <th>Hours</th>
                          <th>Scheduled Date</th>
                          <th>Sheets Needed</th>
                          <th>Material</th>
                          <th>Before</th>
                          <th>After</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {tasksForDay.map((t, i2) => (
                          <StationTaskRow
                            key={t.task_id ?? i2}
                            task={t}
                            onOverride={handleManualOverride}
                            onClearOverride={handleClearOverride}
                            onViewJob={handleViewJob}
                            alertsByJob={alertsByJob}
                            bulkMode={bulkEditMode}
                            selected={selectedTaskIds.has(t.task_id)}
                            onSelectToggle={toggleTaskSelection}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {renderDailyLaserSummary(tasksForDay)}
              </div>
            );
          })}
        </div>
      );
    }
  }

  // =================== All Stations Tab ===================
  function renderAllStationsTab() {
    const tasksByDateStation = {};
    allStationsTasks.forEach((t) => {
      const dk = t.scheduled_date ? t.scheduled_date.slice(0, 10) : "Unscheduled";
      if (!tasksByDateStation[dk]) tasksByDateStation[dk] = {};
      if (!tasksByDateStation[dk][t.station]) {
        tasksByDateStation[dk][t.station] = [];
      }
      tasksByDateStation[dk][t.station].push(t);
    });

    const dayLabel = formatDisplayDate(selectedDate);
    const unschedMap = tasksByDateStation["Unscheduled"] || null;

    function renderAllUnscheduledBlock() {
      if (!unschedMap) return null;
      const stationKeys = Object.keys(unschedMap);
      if (!stationKeys.length) return null;
      return (
        <div
          style={{ marginBottom: "1rem", border: "1px solid #f99", padding: "6px" }}
        >
          <h5>Unscheduled Tasks (All Stations)</h5>
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {stationKeys.map((st, idx) => {
              const arr = unschedMap[st];
              if (!arr || !arr.length) return null;
              return (
                <div key={idx} style={{ marginBottom: "1rem" }}>
                  <div
                    style={{
                      backgroundColor: "#333",
                      color: "#fff",
                      fontSize: "1.2rem",
                      padding: "4px",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                    }}
                  >
                    {st} (Unscheduled) — {arr.length}
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Master Job #</th>
                          <th>Product ID</th>
                          <th>Product Desc</th>
                          <th>Qty</th>
                          <th>Job ID</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Due</th>
                          <th>Hours</th>
                          <th>Sheets Needed</th>
                          <th>Material</th>
                          <th>Before</th>
                          <th>After</th>
                          <th>Override</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {arr.map((task, idx2) => (
                          <StationTaskRow
                            key={idx2}
                            task={task}
                            onOverride={handleManualOverride}
                            onClearOverride={handleClearOverride}
                            onViewJob={handleViewJob}
                            alertsByJob={alertsByJob}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (viewMode === "day") {
      const dayKey = formatDate(selectedDate);
      const stationMap = tasksByDateStation[dayKey] || {};

      return (
        <div>
          <div className="mb-2">
            <strong>All Stations on {dayLabel}</strong>
          </div>

          {renderAllUnscheduledBlock()}

          {stationList.map((st) => {
            const arr = stationMap[st] || [];
            if (!arr.length) return null;
            const stationHours = arr.reduce(
              (acc, tsk) => acc + getHoursFromServer(tsk),
              0
            );
            const stShift = getEffectiveShift(st, selectedDate);
            const stMax = firstShiftHours + (stShift.enabled ? stShift.hours : 0);

            return (
              <div key={st} style={{ marginBottom: "1rem" }}>
                <div
                  style={{
                    backgroundColor: "black",
                    color: stationHours > stMax ? "red" : "white",
                    fontSize: "1.2rem",
                    padding: "4px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    {st} - {stationHours.toFixed(1)} hrs
                    {stShift.enabled && (
                      <span className="badge bg-info ms-2" title={`Second shift +${stShift.hours}h (${stShift.source})`}>
                        +{stShift.hours}h 2nd
                      </span>
                    )}
                  </span>
                  <CapacityGauge hours={stationHours} maxHours={stMax} />
                </div>

                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Master Job #</th>
                        <th>Product ID</th>
                        <th>Product Desc</th>
                        <th>Qty</th>
                        <th>Job ID</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Due</th>
                        <th>Hours</th>
                        <th>Scheduled Date</th>
                        <th>Sheets Needed</th>
                        <th>Material</th>
                        <th>Before</th>
                        <th>After</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {arr.map((task, idx2) => (
                        <StationTaskRow
                          key={idx2}
                          task={task}
                          onOverride={handleManualOverride}
                          onClearOverride={handleClearOverride}
                          onViewJob={handleViewJob}
                          alertsByJob={alertsByJob}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {renderDailyLaserSummary(arr)}
              </div>
            );
          })}
        </div>
      );
    } else {
      // Week
      const weekDates = getWeekDates(selectedDate, showWeekends);

      return (
        <div>
          <strong>All Stations for the selected week</strong>
          {renderAllUnscheduledBlock()}

          {weekDates.map((d) => {
            const dateKey = formatDate(d);
            const stMap = tasksByDateStation[dateKey] || {};

            let totalDayHours = 0;
            stationList.forEach((st) => {
              const arr = stMap[st] || [];
              arr.forEach((tsk) => {
                totalDayHours += getHoursFromServer(tsk);
              });
            });

            return (
              <div
                key={dateKey}
                style={{
                  border: "1px solid #ccc",
                  marginBottom: "1rem",
                  padding: "8px",
                }}
              >
                <div>
                  <strong>
                    {formatDisplayDate(d)}{" "}
                    {isWeekend(d) && (
                      <span className="text-danger">(Weekend)</span>
                    )}
                  </strong>
                </div>
                <div
                  style={{
                    color:
                      totalDayHours > 8 * stationList.length ? "red" : "black",
                  }}
                >
                  {totalDayHours.toFixed(1)} hrs total across all stations
                </div>

                {stationList.map((st) => {
                  const arr = stMap[st] || [];
                  if (!arr.length) return null;
                  const stationHours = arr.reduce(
                    (acc, t) => acc + getHoursFromServer(t),
                    0
                  );
                  const wkStShift = getEffectiveShift(st, d);
                  const wkStMax = firstShiftHours + (wkStShift.enabled ? wkStShift.hours : 0);

                  return (
                    <div key={st} style={{ marginTop: "0.5rem" }}>
                      <div
                        style={{
                          backgroundColor: "black",
                          color: stationHours > wkStMax ? "red" : "white",
                          fontSize: "1.2rem",
                          padding: "4px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginLeft: "1rem",
                          marginRight: "1rem",
                        }}
                      >
                        <span>
                          {st} - {stationHours.toFixed(1)} hrs
                          {wkStShift.enabled && (
                            <span className="badge bg-info ms-2" title={`Second shift +${wkStShift.hours}h (${wkStShift.source})`}>
                              +{wkStShift.hours}h 2nd
                            </span>
                          )}
                        </span>
                        <CapacityGauge hours={stationHours} maxHours={wkStMax} />
                      </div>

                      <div
                        className="table-responsive"
                        style={{ marginLeft: "1rem", marginRight: "1rem" }}
                      >
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Master Job #</th>
                              <th>Product ID</th>
                              <th>Product Desc</th>
                              <th>Qty</th>
                              <th>Job ID</th>
                              <th>Priority</th>
                              <th>Status</th>
                              <th>Due</th>
                              <th>Hours</th>
                              <th>Scheduled Date</th>
                              <th>Sheets Needed</th>
                              <th>Material</th>
                              <th>Before</th>
                              <th>After</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {arr.map((task, i2) => (
                              <StationTaskRow
                                key={i2}
                                task={task}
                                onOverride={handleManualOverride}
                                onClearOverride={handleClearOverride}
                                alertsByJob={alertsByJob}
                                onViewJob={handleViewJob}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {renderDailyLaserSummary(arr)}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      );
    }
  }

  // =================== Job Tab ===================
  function renderJobTab() {
    async function handleLookup() {
        if (!jobIdInput) return;
        setJobLoading(true);
        setJobData(null);

        // The master job # through the job search (summary rows, page by page), else the input as a
        // job id: scheduleData.resolveJobInput.
        const targetJobId = await resolveJobInput(jobIdInput);
        loadJobData(targetJobId);
    }

    return (
      <div>
        <div className="d-flex mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Enter Master Job #"
            value={jobIdInput}
            onChange={(e) => setJobIdInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
            style={{ maxWidth: "250px" }}
          />
          <button
            className="btn btn-primary ms-2"
            onClick={handleLookup}
          >
            Load Job
          </button>
        </div>
        {jobLoading && <div>Loading job data...</div>}
        {jobData && (
          <div>
            <h5>
              Job #{jobData.job_id} (Master #{jobData.master_job_num}) – Priority {jobData.priority_level} – Status {jobData.status}
            </h5>
            <p>
              Due:{" "}
              {jobData.due_date
                ? formatDisplayDate(parseISODate(jobData.due_date))
                : "N/A"}
            </p>

            {!jobData.tasks?.length && <div>No tasks found for this job.</div>}

            {jobScheduleTasks(jobData.tasks) // scheduleRules: no Shipping
              .map((t, idx) => (
                <JobTaskRow
                  key={idx}
                  task={t}
                  job={jobData}
                  onOverride={handleManualOverride}
                  onClearOverride={handleClearOverride}
                />
              ))}
          </div>
        )}
      </div>
    );
  }

  // =================== Part Tab (NEW) ===================
  // This tab allows filtering active tasks by Part ID or Product ID
  function renderPartTab() {   
    return (
      <div>
        <div className="d-flex mb-3 align-items-start">
          <div style={{ position: "relative", width: "300px" }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search Part Number or Description..."
              value={partInput}
              onChange={(e) => handlePartInputChange(e.target.value)}
              onFocus={() => partInput.length >= 2 && setShowSuggestions(true)}
              // Delay blur slightly so the onClick for the list item can fire
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onKeyDown={(e) => e.key === 'Enter' && handlePartLookup()}
              autoComplete="off"
            />
            
            {/* Searchable Dropdown List */}
            {showSuggestions && partSuggestions.length > 0 && (
              <ul 
                className="list-group position-absolute w-100" 
                style={{ 
                  zIndex: 1000, 
                  maxHeight: "300px", 
                  overflowY: "auto", 
                  boxShadow: "0 4px 6px rgba(0,0,0,0.15)",
                  marginTop: "2px"
                }}
              >
                {partSuggestions.map((p) => (
                  <li 
                    key={p.part_id} 
                    className="list-group-item list-group-item-action"
                    style={{ cursor: "pointer" }}
                    // Use onMouseDown to ensure it fires before onBlur
                    onMouseDown={() => selectPartSuggestion(p)}
                  >
                    <strong>{p.product_id}</strong>
                    <br/>
                    <small className="text-muted">{p.description}</small>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            className="btn btn-primary ms-2"
            onClick={handlePartLookup}
          >
            Load Tasks
          </button>
        </div>

        {partLoading && <div>Searching tasks...</div>}
        
        {!partLoading && partTasks.length > 0 && (
          <div>
            <h5>Found {partTasks.length} active tasks for "{partInput}"</h5>
            <div className="table-responsive">
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Job ID</th>
                    <th>Master Job #</th>
                    <th>Station</th>
                    <th>Product ID</th>
                    <th>Desc</th>
                    <th>Status</th>
                    <th>Qty</th>
                    <th>Scheduled</th>
                    <th>Override</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {partTasks.map((task, idx) => (
                    <tr key={idx} className={getTaskColorClass(task, { due_date: task.due_date, priority_level: task.priority_level })}>
                      <td>{task.job_id}</td>
                      <td>{task.master_job_num}</td>
                      <td>{task.station}</td>
                      <td>
                        {task.product_id || (
                          <span className="text-muted" style={{fontSize: "0.85em"}}>
                            {task.contained_parts_str 
                              ? `Batch: ${task.contained_parts_str.substring(0, 25)}...` 
                              : "(Batch Task)"}
                          </span>
                        )}
                      </td>
                      <td style={{maxWidth: "200px"}} className="text-truncate">{task.product_description || task.part_description}</td>
                      <td>{task.task_status}</td>
                      <td>{task.quantity}</td>
                      <td>{task.scheduled_date ? formatDisplayDate(parseISODate(task.scheduled_date)) : "Unscheduled"}</td>
                      <td>
                        <ManualOverrideButton
                            task={task}
                            onOverride={handleManualOverride}
                            onClearOverride={handleClearOverride}
                        />
                      </td>
                      <td>
                        <button 
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleViewJob(task.job_id)}
                        >
                            View Job
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!partLoading && partInput && partTasks.length === 0 && (
            <div className="text-muted">No active tasks found matching "{partInput}".</div>
        )}
      </div>
    );
  }

  // =================== Recommended Tab ===================
  function renderRecommendedTab() {
    const dayLabel = formatDisplayDate(recommendedDate);

    const recommendedMap = {};
    recommendedData.forEach((dayObj) => {
      recommendedMap[dayObj.date] = dayObj.stations;
    });

    if (recommendedViewMode === "day") {
      const dayKey = formatDate(recommendedDate);
      const stArr = recommendedMap[dayKey] || [];
      const sum = stArr.reduce((acc, st) => acc + st.hours, 0);

      return (
        <div>
          <SchedulingFAQ />
          <BacklogPressureBox backlog={backlogPressure} />
          <div className="mb-2" style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <strong>Recommended (Ignoring Normal Constraints) on {dayLabel}</strong>
              <div style={{ color: sum > 8 ? "red" : "black" }}>
                {sum.toFixed(1)} total hours
              </div>
            </div>
            <CapacityGauge hours={sum} maxHours={35} />
          </div>

          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Station</th>
                  <th>Hours Needed</th>
                </tr>
              </thead>
              <tbody>
                {stArr.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="text-muted">
                      No tasks assigned this day in recommended schedule.
                    </td>
                  </tr>
                ) : (
                  stArr.map((s, idx) => {
                    const rowColor = s.hours > 8 ? "text-danger" : "";
                    return (
                      <tr key={idx} className={rowColor}>
                        <td>{s.station}</td>
                        <td>{s.hours.toFixed(1)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <p className="text-muted">
            *Red indicates station hours exceed normal 8-hr shift.
          </p>
        </div>
      );
    } else {
      const weekDates = getWeekDates(recommendedDate, recommendedShowWeekends);

      return (
        <div>
          <SchedulingFAQ />
          <BacklogPressureBox backlog={backlogPressure} />
          <strong>Recommended Schedule for the selected week (Deadline-Driven)</strong>
          {weekDates.map((d) => {
            const dateKey = formatDate(d);
            const stArr = recommendedMap[dateKey] || [];
            const sum = stArr.reduce((acc, st) => acc + st.hours, 0);

            return (
              <div
                key={dateKey}
                style={{ border: "1px solid #ccc", marginBottom: "1rem", padding: "8px" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <strong>
                      {formatDisplayDate(d)}{" "}
                      {isWeekend(d) && <span className="text-danger">(Weekend)</span>}
                    </strong>
                    <div style={{ color: sum > 8 ? "red" : "black" }}>
                      {sum.toFixed(1)} total hours
                    </div>
                  </div>
                  <CapacityGauge hours={sum} maxHours={35} />
                </div>

                {stArr.length === 0 ? (
                  <div className="text-muted mt-1">No tasks scheduled.</div>
                ) : (
                  <div className="table-responsive mt-1">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Station</th>
                          <th>Hours Needed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stArr.map((s, idx2) => {
                          const rowColor = s.hours > 8 ? "text-danger" : "";
                          return (
                            <tr key={idx2} className={rowColor}>
                              <td>{s.station}</td>
                              <td>{s.hours.toFixed(1)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
          <p className="text-muted">
            *Red indicates station hours exceed normal 8-hr shift.
          </p>
        </div>
      );
    }
  }

  // =========== USE EFFECTS FOR TAB CHANGES ===========

  useEffect(() => {
    if (activeTab === "station") {
      loadStationQueue(selectedStation);
    } else if (activeTab === "all") {
      loadAllStations();
    } else if (activeTab === "recommended") {
      loadRecommendedSchedule();
    }
    // "part" tab doesn't load immediately; waits for user input
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "station") {
      loadStationQueue(selectedStation);
    }
  }, [selectedStation]);

  useEffect(() => {
    if (activeTab === "recommended") {
      loadRecommendedSchedule();
      fetch(`${API_BASE}/backlog_pressure`)
        .then((res) => res.json())
        .then((data) => setBacklogPressure(data))
        .catch((err) =>
          console.error("Failed to fetch backlog pressure:", err)
        );
    }
  }, [activeTab, recommendedDate, recommendedViewMode, recommendedShowWeekends, forecastType]);
  

  return (
    <>
      <div className="container-fluid mt-4">
        <h2>Scheduling Page</h2>

        <SchedulingAlertsBox alerts={schedulingAlerts} />
        <PastDueInboundBanner items={pastDueInbound} />

        <div className="btn-group mb-3">
          <button
            className={`btn btn-${activeTab === "station" ? "primary" : "outline-primary"}`}
            onClick={() => setActiveTab("station")}
          >
            Station-Based
          </button>
          <button
            className={`btn btn-${activeTab === "all" ? "primary" : "outline-primary"}`}
            onClick={() => setActiveTab("all")}
          >
            All Stations
          </button>
          <button
            className={`btn btn-${activeTab === "job" ? "primary" : "outline-primary"}`}
            onClick={() => setActiveTab("job")}
          >
            Job-Based
          </button>
          {/* NEW Part-Based Tab Button */}
          <button
            className={`btn btn-${activeTab === "part" ? "primary" : "outline-primary"}`}
            onClick={() => setActiveTab("part")}
          >
            Part-Based
          </button>
          <button
            className={`btn btn-${activeTab === "recommended" ? "primary" : "outline-primary"}`}
            onClick={() => setActiveTab("recommended")}
          >
            Recommended
          </button>
        </div>

        {/* Shared top controls */}
        <div>
          <button className="btn btn-warning me-3" onClick={handleFullSchedule} disabled={isScheduling}>
            {isScheduling ? "Scheduling..." : "Auto-Schedule All"}
          </button>

          {activeTab === "station" || activeTab === "all" ? (
            <>
              <div className="d-inline-block me-4">
                <label className="me-2">View:</label>
                <select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  className="me-3"
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                </select>

                <input
                  type="date"
                  value={formatDate(selectedDate)}
                  onChange={(e) => {
                    const dt = new Date(e.target.value + "T00:00:00");
                    setSelectedDate(dt);
                  }}
                />
              </div>

              {viewMode === "week" && (
                <div className="form-check form-switch d-inline-block align-middle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="weekendSwitch"
                    checked={showWeekends}
                    onChange={(e) => setShowWeekends(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="weekendSwitch">
                    Show Weekends
                  </label>
                </div>
              )}

              {activeTab === "station" && (
                <div className="d-inline-block ms-3">
                  <label className="me-2">Station:</label>
                  <select
                    value={selectedStation}
                    onChange={(e) => setSelectedStation(e.target.value)}
                  >
                    {stationList.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {activeTab === "station" && (
                <button
                  className={`btn ms-3 ${bulkEditMode ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={toggleBulkMode}
                  title="Toggle bulk-edit mode to select multiple tasks and reschedule them together"
                >
                  {bulkEditMode ? "Exit Bulk Edit" : "Bulk Edit"}
                </button>
              )}
            </>
          ) : null}

          {activeTab === "station" && bulkEditMode && (
            <div
              className="mt-2 p-2 border rounded bg-light d-flex flex-wrap align-items-center"
              style={{ gap: "8px" }}
            >
              <strong className="me-2">
                {selectedTaskIds.size} task{selectedTaskIds.size === 1 ? "" : "s"} selected
              </strong>

              <label className="ms-2 mb-0">New date:</label>
              <input
                type="date"
                value={bulkDate}
                onChange={(e) => setBulkDate(e.target.value)}
                disabled={bulkBusy}
                style={{ width: "150px" }}
              />
              <button
                className="btn btn-sm btn-primary"
                onClick={applyBulkDate}
                disabled={bulkBusy || selectedTaskIds.size === 0 || !bulkDate}
                title="Override the scheduled date on every selected task"
              >
                {bulkBusy ? "Applying..." : `Apply to ${selectedTaskIds.size} selected`}
              </button>

              <button
                className="btn btn-sm btn-outline-danger"
                onClick={applyBulkClear}
                disabled={bulkBusy || selectedTaskIds.size === 0}
                title="Remove manual override on every selected task — they will be auto-scheduled"
              >
                Clear {selectedTaskIds.size} override{selectedTaskIds.size === 1 ? "" : "s"}
              </button>

              <button
                className="btn btn-sm btn-link"
                onClick={clearSelection}
                disabled={bulkBusy || selectedTaskIds.size === 0}
              >
                Clear selection
              </button>

              <span className="text-muted ms-auto" style={{ fontSize: "0.85em" }}>
                Tip: use the checkbox in any day header to select every task in that day.
              </span>
            </div>
          )}

          {activeTab === "recommended" && (
            <>
              <div className="d-inline-block me-4">
                  <strong className="me-2">Forecast Type:</strong>
                  <div className="form-check form-check-inline">
                      <input className="form-check-input" type="radio" name="forecastType" id="deadlineRadio" value="deadline" checked={forecastType === 'deadline'} onChange={(e) => setForecastType(e.target.value)} />
                      <label className="form-check-label" htmlFor="deadlineRadio">Deadline-Driven</label>
                  </div>
                  <div className="form-check form-check-inline">
                      <input className="form-check-input" type="radio" name="forecastType" id="leveledRadio" value="leveled" checked={forecastType === 'leveled'} onChange={(e) => setForecastType(e.target.value)} />
                      <label className="form-check-label" htmlFor="leveledRadio">Leveled Plan</label>
                  </div>
              </div>

              <div className="d-inline-block me-4">
                <label className="me-2">View:</label>
                <select
                  value={recommendedViewMode}
                  onChange={(e) => setRecommendedViewMode(e.target.value)}
                  className="me-3"
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                </select>

                <input
                  type="date"
                  value={formatDate(recommendedDate)}
                  onChange={(e) => {
                    setRecommendedDate(new Date(e.target.value + "T00:00:00"));
                  }}
                />
              </div>

              {recommendedViewMode === "week" && (
                <div className="form-check form-switch d-inline-block align-middle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="recommendedWeekendSwitch"
                    checked={recommendedShowWeekends}
                    onChange={(e) => setRecommendedShowWeekends(e.target.checked)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="recommendedWeekendSwitch"
                  >
                    Show Weekends
                  </label>
                </div>
              )}
            </>
          )}
        </div>

        <hr />

        {activeTab === "station" && stationLoading && <div>Loading station tasks...</div>}
        {activeTab === "station" && !stationLoading && renderStationTab()}

        {activeTab === "all" && allStationsLoading && <div>Loading all stations tasks...</div>}
        {activeTab === "all" && !allStationsLoading && renderAllStationsTab()}

        {activeTab === "job" && renderJobTab()}

        {/* NEW Part Tab Render */}
        {activeTab === "part" && renderPartTab()}

        {activeTab === "recommended" && (
          <div>
            {recommendedLoading ? (
              <div>Loading recommended schedule...</div>
            ) : (
              renderRecommendedTab()
            )}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * StationTaskRow
 * Renders each row in station-based or all-stations tables.
 */
// Sortable wrapper around StationTaskRow used by the Day view's DndContext.
// Pulls drag handle attributes/listeners from useSortable and forwards them to
// the row's drag-handle <td>. The rest of the row stays interactive (button
// clicks, inputs, etc.) because the activationConstraint requires a 6px drag
// before lifting the row.
function SortableStationTaskRow(props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.task.task_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    backgroundColor: isDragging ? "var(--jq-bg-subtle, #f8f9fa)" : undefined,
  };

  return (
    <StationTaskRow
      {...props}
      rowRef={setNodeRef}
      rowStyle={style}
      dragHandleProps={{ ...attributes, ...listeners }}
    />
  );
}


function StationTaskRow({ task, onOverride, onClearOverride, onViewJob, alertsByJob, rowRef, rowStyle, dragHandleProps, bulkMode, selected, onSelectToggle }) {
  const colorClass = getTaskColorClass(task, task);

  let { product_id, product_description } = task;
  if (task.workstation === "Laser" && !task.part_id) {
    if (task.primary_part_ordered) {
      product_description = task.primary_part_ordered;
    } else if (task.job_description) {
      product_description = task.job_description;
    }
  }

  const masterJobNum = task.master_job_num || "";
  const productId = product_id || "";
  const qty = task.quantity || 0;
  const status = task.task_status || "";
  const priority = task.priority_level || "";
  const dueDate = task.due_date ? formatDisplayDate(parseISODate(task.due_date)) : "";
  const hours = getHoursFromServer(task);

  const schedStr = task.scheduled_date
    ? formatDisplayDate(parseISODate(task.scheduled_date))
    : "";

  const sheetsNeeded =
    task.workstation === "Laser" && task.sheet_required ? task.sheet_required : 0;
  const matDesc =
    task.workstation === "Laser" && task.material_description
      ? task.material_description
      : "";
  const beforeVal =
    task.workstation === "Laser" && task.available_before_task != null
      ? task.available_before_task
      : "";
  const afterVal =
    task.workstation === "Laser" && task.available_after_task != null
      ? task.available_after_task
      : "";

  const jobAlerts = alertsByJob?.[task.job_id] || [];
  const hasAlerts = jobAlerts.length > 0;

  function handleAlertClick() {
    const displayMsg = jobAlerts
      .map((a) => `${a.alert_type} - ${a.message}`)
      .join("\n\n");
    alert(displayMsg);
  }

  const isPinned = task.manual_sort_position != null;

  return (
    <tr ref={rowRef} className={colorClass} style={rowStyle}>
      {bulkMode ? (
        <td
          style={{ width: 28, textAlign: "center", verticalAlign: "middle" }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onSelectToggle && onSelectToggle(task.task_id)}
            aria-label={`Select task ${task.task_id}`}
          />
        </td>
      ) : null}
      {dragHandleProps ? (
        <td
          {...dragHandleProps}
          style={{
            cursor: "grab",
            textAlign: "center",
            color: isPinned ? "#0a7" : "#999",
            fontWeight: "bold",
            userSelect: "none",
            verticalAlign: "middle",
          }}
          title={isPinned ? `📌 Pinned at position ${task.manual_sort_position}. Drag to reorder.` : "Drag to set manual order"}
        >
          {isPinned ? "📌" : "⋮⋮"}
        </td>
      ) : null}
      <td>{masterJobNum}</td>
      <td style={{ maxWidth: "80px" }}>{productId}</td>
      <td style={{ maxWidth: "140px" }}>{product_description || ""}</td>
      <td>{qty}</td>
      <td>{task.job_id}</td>
      <td>{priority}</td>
      <td>{status}</td>
      <td>{dueDate}</td>
      <td>{hours.toFixed(1)}</td>
      <td>{schedStr}</td>

      {/* Laser columns */}
      <td>{sheetsNeeded}</td>
      <td>{matDesc}</td>
      <td>{beforeVal}</td>
      <td>{afterVal}</td>

      <td>
        <ManualOverrideButton task={task} onOverride={onOverride} onClearOverride={onClearOverride} />
      </td>
      <td style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {onViewJob && (
          <button
            className="btn btn-sm btn-primary"
            onClick={() => onViewJob(task.job_id)}
          >
            View Job
          </button>
        )}
        {hasAlerts && (
          <button
            onClick={handleAlertClick}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "1.2rem",
            }}
            title="View Alerts for this job"
          >
            ⚠️
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * JobTaskRow
 * Shows tasks for the Job-based tab.
 */
function JobTaskRow({ task, job, onOverride, onClearOverride }) {
  const colorClass = getTaskColorClass(task, job);
  const schedStr = task.scheduled_date
    ? formatDisplayDate(parseISODate(task.scheduled_date))
    : "";
  const status = task.task_status || "";

  const partDisplay = task.product_id 
    ? `${task.product_id} - ${task.part_description || ""}` 
    : (task.part_description || "N/A");

  return (
    <div
      className={`p-2 border-bottom d-flex align-items-center ${colorClass}`}
      style={{ gap: "20px" }}
    >
      <div style={{ minWidth: "120px", fontWeight: "bold" }}>{task.workstation}</div>
      
      <div style={{ minWidth: "200px", maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={partDisplay}>
        <small className="text-muted">Part:</small> {partDisplay}
      </div>

      <div style={{ minWidth: "120px" }}>
         <small className="text-muted">Status:</small> {status}
      </div>
      <div style={{ minWidth: "150px" }}>
        <small className="text-muted">Sched:</small> {schedStr || "Unscheduled"}
      </div>
      <ManualOverrideButton task={task} onOverride={onOverride} onClearOverride={onClearOverride} />
    </div>
  );
}

function ManualOverrideButton({ task, onOverride, onClearOverride }) {
  const [overrideDate, setOverrideDate] = useState(
    task.scheduled_date ? task.scheduled_date.slice(0, 10) : ""
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleClick() {
    if (!overrideDate) return;
    if (!window.confirm(`Override this task's schedule to ${overrideDate}?`)) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onOverride(task.task_id, overrideDate);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleClear() {
    setIsSubmitting(true);
    try {
      await onClearOverride(task.task_id);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
      <input
        type="date"
        value={overrideDate}
        onChange={(e) => setOverrideDate(e.target.value)}
        style={{ width: "120px" }}
      />
      <button
        className="btn btn-sm btn-outline-secondary"
        onClick={handleClick}
        disabled={isSubmitting}
      >
        {isSubmitting ? "..." : "Override"}
      </button>
      {task.manual_override && onClearOverride && (
        <button
          className="btn btn-sm btn-outline-danger"
          onClick={handleClear}
          disabled={isSubmitting}
          title="Remove manual override"
        >
          {isSubmitting ? "..." : "Clear"}
        </button>
      )}
    </div>
  );
}